import { useQueryClient } from 'react-query';

import { useApiMutation, useApiQuery } from '../../api/useApi';
import type { Customer } from '../../pages/cardex/api/useCustomer';

import { estRequeteConversations } from './useConversations';

/**
 * Fermer et rouvrir une conversation — #85, décision #30.
 *
 * **Fermer, et rien d'autre.** C'est le seul geste de dossier réellement utilisé
 * dans le parc : **1 347** conversations dans « Fermé », contre 97 en spam, 41 en
 * corbeille et **21** réparties dans les 1 270 dossiers libres provisionnés
 * d'office. La taxonomie a été offerte toute faite et n'a pas été adoptée. Spam,
 * corbeille et dossiers libres restent au bureau.
 *
 * Techniquement, fermer = **attacher le dossier système `closed`**, rouvrir = le
 * détacher. Rien à inventer côté API.
 *
 * **L'état se lit sans requête** : `/customer-basics` charge déjà `folders`
 * (`CustomerController.php:994`), et #83 appelle cet endpoint pour nommer le
 * client. Le fil sait donc s'il est fermé avant même d'y penser.
 */

/** Une conversation est fermée si elle porte le dossier système `closed`. */
export function estFermee(client: Customer | undefined): boolean {
    return (client?.folders ?? []).some((dossier) => dossier.slug === 'closed');
}

/**
 * L'identifiant du dossier `closed` de l'établissement.
 *
 * Chargé **à l'ouverture du fil**, pas au premier tap : sinon le geste attendrait
 * un aller-retour réseau avant de faire quoi que ce soit. C'est une donnée de
 * configuration, mise en cache longuement — elle ne change jamais.
 */
interface DossierServeur {
    id: number;
    slug: string;
}

export function useIdDossierFerme(facilityId: number | null): number | null {
    const dossiers = useApiQuery<DossierServeur[]>(
        facilityId !== null ? `/folders?facility_id=${facilityId}` : '',
        {},
        { staleTime: 30 * 60 * 1000 },
    );

    // `Array.isArray` et non `?? []` : une réponse d'une autre forme ferait planter
    // le `.find`, et emporterait l'écran entier pour une donnée qui n'est que la
    // configuration d'un bouton. Sans identifiant, le contrôle ne s'affiche pas —
    // c'est le pire acceptable.
    const liste = Array.isArray(dossiers.data) ? dossiers.data : [];
    return liste.find((d) => d.slug === 'closed')?.id ?? null;
}

/**
 * Ferme ou rouvre.
 *
 * L'invalidation de `/customers-messaging` suffit à faire disparaître la
 * conversation de la boîte : `scopeInInbox()` la retire côté serveur dès qu'elle
 * porte un dossier `hides_from_inbox`. **Aucun filtre client**, et pas de
 * rechargement complet — c'est le critère d'acceptation, et il tombe tout seul.
 */
export function useFermerConversation(customerId: number | string) {
    const queryClient = useQueryClient();

    const rafraichir = () => {
        queryClient.invalidateQueries(`/customer-basics/${customerId}`);
        queryClient.invalidateQueries({ predicate: estRequeteConversations });
    };

    const fermer = useApiMutation<unknown, unknown, { folderId: number }>(
        'post',
        ({ folderId }) => `/customers/${customerId}/folders/${folderId}`,
        { onSuccess: rafraichir },
    );

    const rouvrir = useApiMutation<unknown, unknown, { folderId: number }>(
        'delete',
        ({ folderId }) => `/customers/${customerId}/folders/${folderId}`,
        { onSuccess: rafraichir },
    );

    return { fermer, rouvrir };
}
